import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useLang, STR, loc } from "@/i18n/translations";
import { ArtistPhoto, ArtistOverlay } from "@/components/ArtistPhoto";
import { SocialLinks } from "@/components/SocialIcons";
import api from "@/lib/api";

export default function Artists() {
  const { t, lang } = useLang();
  const [artists, setArtists] = useState([]);

  useEffect(() => {
    api.get("/artists").then((r) => setArtists(r.data)).catch(() => {});
  }, []);

  return (
    <div className="pt-32 pb-24">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <p className="eyebrow mb-5">{t(STR.nav.artists)}</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold">{t(STR.home.artistsTitle)}</h1>
        <div className="gold-line w-24 my-10" />

        <p className="text-cream/70 text-lg leading-relaxed max-w-3xl mb-12" data-testid="artists-intro">
          {t({
            fr: "Sily Agency accompagne des artistes aux univers singuliers, entre musiques africaines, urbaines et contemporaines. Des projets pensés pour la scène, les festivals et les événements culturels, en France comme à l'international.",
            en: "Sily Agency supports artists with singular universes, blending African, urban and contemporary music. Projects designed for the stage, festivals and cultural events, in France and internationally.",
          })}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {artists.map((a, i) => (
            <motion.div
              key={a.id}
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              data-testid={`artist-card-${a.slug}`}
              className="relative"
            >
              <SocialLinks links={a.socials} owner={a.name} prefix={`artist-card-${a.slug}`} className="absolute top-4 right-4 !gap-2 z-20 justify-end max-w-[70%]"
                itemClass="w-9 h-9 rounded-full bg-obsidian/80 backdrop-blur border border-gold/30 flex items-center justify-center text-cream hover:text-gold hover:border-gold transition-colors" />
              <Link to={`/artistes/${a.slug}`} className="group block relative rounded-2xl overflow-hidden aspect-[3/4] border border-gold/15">
                <ArtistPhoto src={a.image} alt={a.name} position={a.image_position} className="transition-transform duration-700 group-hover:scale-110" />
                <ArtistOverlay />
                <div className="absolute bottom-0 p-6 w-full text-shadow-strong">
                  <h2 className="font-serif text-4xl font-semibold text-white leading-tight" data-testid={`artist-card-name-${a.slug}`}>{a.name}</h2>
                  <p className="mt-2 text-sm text-gold-light font-semibold tracking-wide leading-snug" data-testid={`artist-card-genre-${a.slug}`}>{loc(a, "genre", lang)}</p>
                  <span className="mt-3 inline-flex items-center gap-2 text-sm text-white group-hover:text-gold transition-colors">
                    {t(STR.common.bookThisArtist)} <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
